import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { X } from 'lucide-react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  OutboundCallType,
  type DelinquencyAgentMetadata,
} from '@super/shared-types'
import {
  parsePhoneNumberWithError,
  isValidPhoneNumber,
} from 'libphonenumber-js'

const INSTRUCTIONS_STORAGE_KEY = 'simulate_delinquency_instructions'

const simulateDelinquencySchema = z.object({
  customerName: z.string().min(1, 'Customer name is required'),
  phone: z.string().refine(val => {
    try {
      // Allow both US and German phone numbers
      return isValidPhoneNumber(val, 'US') || isValidPhoneNumber(val, 'DE')
    } catch {
      return false
    }
  }, 'Valid phone number is required'),
  property: z.string().min(1, 'Property is required'),
  rentAmount: z.string().min(1, 'Rent amount is required'),
  rentDueDate: z.string().min(1, 'Rent due date is required'),
  overdueAmount: z.string().min(1, 'Overdue amount is required'),
  daysOverdue: z.coerce.number().min(0, 'Days overdue must be 0 or greater'),
  instructions: z.string().optional(),
})

type SimulateDelinquencyFormValues = z.infer<typeof simulateDelinquencySchema>

interface SimulateDelinquencyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (data: SimulateDelinquencyFormValues) => Promise<void>
}

// Phone number formatting helper
function formatPhoneNumber(value: string): string {
  // If it starts with +, it's likely an international number - don't format it
  if (value.startsWith('+')) {
    // Allow user to type freely, just clean up extra spaces
    return value.replace(/\s+/g, ' ').trim()
  }

  // Remove all non-numeric characters for US number detection
  const numbers = value.replace(/\D/g, '')

  // If it's longer than 10 digits, it's likely international - don't format
  if (numbers.length > 10) {
    return value // Return as-is for international numbers
  }

  // Apply (XXX) XXX-XXXX format only for US numbers (10 digits)
  if (numbers.length === 0) return ''
  if (numbers.length <= 3) return `(${numbers}`
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`
}

export function SimulateDelinquencyModal({
  open,
  onOpenChange,
  onSubmit: onSubmitProp,
}: SimulateDelinquencyModalProps) {
  const { currentCompany } = useAppAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [accordionValue, setAccordionValue] = useState<string>('')
  const [storedInstructions, setStoredInstructions] = useState<string>('')

  const form = useForm<SimulateDelinquencyFormValues>({
    resolver: zodResolver(simulateDelinquencySchema),
    defaultValues: {
      customerName: '',
      phone: '',
      property: '',
      rentAmount: '',
      rentDueDate: '',
      overdueAmount: '',
      daysOverdue: 0,
      instructions: '',
    },
  })

  // Load instructions from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(INSTRUCTIONS_STORAGE_KEY)
    if (saved) {
      setStoredInstructions(saved)
      form.setValue('instructions', saved)
    }
  }, [form])

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem(INSTRUCTIONS_STORAGE_KEY)
      form.reset({
        customerName: '',
        phone: '',
        property: '',
        rentAmount: '',
        rentDueDate: '',
        overdueAmount: '',
        daysOverdue: 0,
        instructions: saved || '',
      })
      setStoredInstructions(saved || '')
      setAccordionValue('') // Keep accordion closed
    }
  }, [open, form])

  // Save instructions to localStorage when they change
  const handleInstructionsChange = (value: string) => {
    if (value.trim()) {
      localStorage.setItem(INSTRUCTIONS_STORAGE_KEY, value)
      setStoredInstructions(value)
    }
  }

  // Clear instructions
  const handleClearInstructions = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent accordion from toggling
    localStorage.removeItem(INSTRUCTIONS_STORAGE_KEY)
    setStoredInstructions('')
    form.setValue('instructions', '')
    setAccordionValue('instructions') // Open accordion for input
  }

  const onSubmit: SubmitHandler<SimulateDelinquencyFormValues> = async data => {
    if (!currentCompany?.id) {
      toast.error('Company ID is missing')
      return
    }
    if (!currentCompany?.phone?.trim()) {
      toast.error(
        'Company phone is required for outbound calls. Please configure a phone number in company settings.',
      )
      return
    }

    setIsSubmitting(true)
    try {
      if (onSubmitProp) {
        await onSubmitProp(data)
      } else {
        // Convert phone to E.164 format
        let e164Phone = data.phone
        try {
          // Try US first, then German
          let phoneNumber
          try {
            phoneNumber = parsePhoneNumberWithError(data.phone, 'US')
          } catch {
            phoneNumber = parsePhoneNumberWithError(data.phone, 'DE')
          }
          e164Phone = phoneNumber.format('E.164')
        } catch (error) {
          console.error('Failed to parse phone number:', error)
        }

        // Dispatch LiveKit agent for outbound delinquency
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6060'
        const roomName = `delinquency-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`

        const prepareRes = await fetch(
          `${apiUrl}/api/voice/conferences/outbound/prepare`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              to: e164Phone,
              companyId: currentCompany.id,
              callId: roomName,
            }),
          },
        )
        if (!prepareRes.ok) {
          throw new Error(
            `Failed to prepare outbound session: ${prepareRes.statusText}`,
          )
        }
        const {
          data: { sessionId },
        } = await prepareRes.json()

        // Prepare metadata for agent (matching Python agent's expected format)
        const metadata: DelinquencyAgentMetadata = {
          callType: OutboundCallType.DELINQUENCY,
          phoneTree: [e164Phone],
          transfer_to: null,
          customerName: data.customerName,
          property: data.property,
          rentAmount: data.rentAmount,
          rentDueDate: data.rentDueDate,
          overdueAmount: data.overdueAmount,
          daysOverdue: data.daysOverdue,
          companyId: currentCompany.id,
          companyName: currentCompany.name,
          instructions: data.instructions,
          from: currentCompany.phone,
          sessionId,
          variableValues: {
            customerName: data.customerName,
            companyName: currentCompany.name,
            agentName: currentCompany.name,
            property: data.property,
            rentAmount: data.rentAmount,
            rentDueDate: data.rentDueDate,
            overdueAmount: data.overdueAmount,
          },
        }

        const response = await fetch(`${apiUrl}/api/livekit/dispatch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            agentName: 'outbound-caller',
            roomName,
            metadata,
            envPrefix: 'DELINQUENCY_OUTBOUND',
          }),
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.error('Agent dispatch error:', errorText)
          throw new Error(`Failed to dispatch agent: ${response.statusText}`)
        }

        const result = await response.json()
        console.log('✅ Delinquency agent dispatched successfully:', result)
        console.log(
          '📞 Outbound agent is now running and will call:',
          metadata.phoneTree[0],
        )

        toast.success('Delinquency agent dispatched successfully')
      }

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to dispatch delinquency agent:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to dispatch delinquency agent',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Simulate Delinquency Call</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="customerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Lindsay Liu" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+49 152 2390187 or (555) 123-4567"
                      {...field}
                      onChange={e => {
                        const formatted = formatPhoneNumber(e.target.value)
                        field.onChange(formatted)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="property"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Property</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main Street, Unit 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="rentAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rent Amount</FormLabel>
                    <FormControl>
                      <Input placeholder="$1,750" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rentDueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rent Due Date</FormLabel>
                    <FormControl>
                      <Input placeholder="January 1, 2026" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="overdueAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Overdue Amount</FormLabel>
                    <FormControl>
                      <Input placeholder="$1,750" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="daysOverdue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Days Overdue</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="1"
                        {...field}
                        onChange={e => {
                          const value = e.target.value
                          field.onChange(value === '' ? 0 : parseInt(value, 10))
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Accordion
              type="single"
              collapsible
              value={accordionValue}
              onValueChange={setAccordionValue}
            >
              <AccordionItem value="instructions" className="border-none">
                <div className="flex items-center gap-2">
                  <AccordionTrigger className="flex-1 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Instructions</span>
                      {storedInstructions && (
                        <span className="text-xs text-muted-foreground">
                          • Saved
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  {storedInstructions && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleClearInstructions}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <AccordionContent className="px-0 pb-0 pt-0">
                  <FormField
                    control={form.control}
                    name="instructions"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder="Add special instructions for this call..."
                            rows={4}
                            {...field}
                            onBlur={e => {
                              field.onBlur()
                              handleInstructionsChange(e.target.value)
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Simulating...' : 'Simulate'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
